import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { Spinner } from '../styles/components';
import { useAsync } from '../hooks/useAsync';
import { formatTooltipValue, formatSharePercent } from '../utils/formatters';
import { SONSTIGES_COLOR } from '../utils/colors';
import type { BudgetApiClient } from '../api/client';
import { useT } from '../i18n';
import type { FilterState, SonstigesTitle } from '../types';

// Same phone breakpoint the FilterPanel uses to switch to a bottom sheet, so the
// picker feels native to whichever surface it appears on.
const PHONE = '@media (max-width: 600px), (max-height: 600px) and (orientation: landscape)';

// Desktop popover geometry. Anchored near the Sonstiges click, flipping sides /
// clamping to stay on-screen — mirrors TitlePointPopover.
const POPOVER_WIDTH = 380;
const POPOVER_MAX_HEIGHT = 460;
const ANCHOR_OFFSET = 12;

// The picker's whole point is to "open the grey box": a title's share of
// Sonstiges shows as a bar that is grey while bucketed and takes the accent once
// broken out — a visible promotion out of the anonymous grey.
const NEUTRAL_BAR = SONSTIGES_COLOR;
const ACTIVE_BAR = 'var(--primary-color)';

// Backdrop: invisible on desktop (click-outside is handled by a listener), a
// dimming scrim on phones where the picker is a bottom sheet.
const Scrim = styled.div<{ isVisible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: transparent;
  ${PHONE} {
    background: rgba(24, 28, 68, 0.4);
    opacity: ${props => (props.isVisible ? 1 : 0)};
    transition: opacity 0.28s ease;
    @media (prefers-reduced-motion: reduce) { transition: none; }
  }
`;

// Positioning uses CSS custom properties (not inline top/left) so the PHONE
// media query can fully override to a bottom sheet — inline styles would beat
// the media rule and strand the sheet at the anchor point.
const Container = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: var(--anchor-top);
  left: var(--anchor-left);
  width: ${POPOVER_WIDTH}px;
  max-width: calc(100vw - 24px);
  max-height: ${POPOVER_MAX_HEIGHT}px;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.surface};
  border-radius: var(--radius-s);
  box-shadow: var(--shadow-m);
  z-index: 9999;
  overflow: hidden;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  transition: opacity 0.15s ease;

  ${PHONE} {
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: none;
    max-height: 70vh;
    border-radius: var(--radius-l) var(--radius-l) 0 0;
    box-shadow: 0 -6px 24px rgba(24, 28, 68, 0.22);
    opacity: 1;
    visibility: visible;
    transform: ${props => (props.isVisible ? 'translateY(0)' : 'translateY(100%)')};
    transition: transform 0.28s ease;
  }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.sm};
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  line-height: 1.3;
`;

const Subtitle = styled.div`
  font-family: var(--sans-stack);
  font-size: 0.85rem;
  color: ${theme.colors.text.muted};
  line-height: 1.3;
  margin-top: 2px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  font-size: 16px;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.08);
    color: ${theme.colors.text.primary};
  }
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin: 0 ${theme.spacing.md} ${theme.spacing.sm};
  padding: 6px 10px;
  border: 1px solid var(--color-bg-muted);
  border-radius: var(--radius-m);
  color: ${theme.colors.text.muted};

  &:focus-within {
    border-color: var(--primary-color);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--sans-stack);
  font-size: 0.95rem;
  color: ${theme.colors.text.primary};
`;

const List = styled.div`
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 ${theme.spacing.sm} ${theme.spacing.sm};
`;

const Row = styled.button<{ active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: ${props => (props.active ? 'rgba(24, 28, 68, 0.05)' : 'transparent')};
  border-radius: var(--radius-s);
  padding: 8px 8px 10px;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover { background: rgba(24, 28, 68, 0.06); }
  &:focus-visible { outline: 2px solid var(--primary-color); outline-offset: -2px; }
`;

const RowTop = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
`;

const RowName = styled.span`
  flex: 1;
  min-width: 0;
  font-family: var(--sans-stack);
  font-size: 0.95rem;
  color: ${theme.colors.text.primary};
  line-height: 1.25;
  word-break: break-word;
`;

// Two left-aligned numeric columns; the widths are shared with the header row so
// labels sit directly above their values. SHARE_COL is wide enough for the
// two-line "Anteil an Sonstiges" header.
const IST_COL = '90px';
const SHARE_COL = '88px';

const RowIst = styled.span`
  width: ${IST_COL};
  flex-shrink: 0;
  text-align: left;
  font-family: var(--sans-stack);
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const RowShare = styled.span`
  width: ${SHARE_COL};
  flex-shrink: 0;
  text-align: left;
  font-family: var(--sans-stack);
  font-size: 0.9rem;
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  font-variant-numeric: tabular-nums;
`;

const Toggle = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  color: ${props => (props.active ? '#fff' : 'var(--primary-color)')};
  background: ${props => (props.active ? 'var(--primary-color)' : 'transparent')};
  border: 1px solid var(--primary-color);
`;

const ShareTrack = styled.div`
  height: 4px;
  margin-top: 6px;
  border-radius: 2px;
  background: rgba(24, 28, 68, 0.06);
  overflow: hidden;
`;

const ShareFill = styled.div<{ active: boolean; pct: number }>`
  height: 100%;
  width: ${props => Math.max(props.pct, 2)}%;
  background: ${props => (props.active ? ACTIVE_BAR : NEUTRAL_BAR)};
  border-radius: 2px;
  transition: width 0.2s ease, background 0.2s ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

// Column headers so the two numbers read clearly. Rendered as a sticky row
// INSIDE the scrolling List so it shares the rows' exact width and scrollbar
// gutter — otherwise the fixed-width value columns drift out from under their
// labels. Horizontal padding matches a Row's 8px so the columns line up.
// align-items:flex-end bottom-aligns a wrapped Ist-Wert label with the others.
const HeaderRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  gap: ${theme.spacing.sm};
  padding: 2px 8px 6px;
  background: ${theme.colors.surface};
  border-bottom: 1px solid var(--color-bg-muted);
`;

const HeaderLabel = styled.span`
  font-family: var(--sans-stack);
  font-size: 0.68rem;
  font-weight: ${theme.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${theme.colors.text.muted};
  line-height: 1.2;
`;

const HeaderName = styled(HeaderLabel)`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const HeaderIst = styled(HeaderLabel)`
  width: ${IST_COL};
  flex-shrink: 0;
  text-align: left;
`;

const HeaderShare = styled(HeaderLabel)`
  width: ${SHARE_COL};
  flex-shrink: 0;
  text-align: left;
`;

// Spacer matching the Toggle so the header columns align with the row columns.
const HeaderToggleSpacer = styled.span`
  width: 22px;
  flex-shrink: 0;
`;

const StateMsg = styled.div`
  font-family: var(--sans-stack);
  font-size: 0.9rem;
  color: ${theme.colors.text.muted};
  text-align: center;
  padding: ${theme.spacing.lg} ${theme.spacing.md};
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: ${theme.spacing.lg};
`;

// Sticky footer — mainly the phone sheet's "Fertig"; on desktop it just shows
// the selected count.
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-top: 1px solid var(--color-bg-muted);
  background: ${theme.colors.surface};
`;

const FooterCount = styled.span`
  font-family: var(--sans-stack);
  font-size: 0.85rem;
  color: ${theme.colors.text.muted};
`;

const DoneButton = styled.button`
  border: none;
  background: var(--primary-color);
  color: #fff;
  font-family: var(--sans-stack);
  font-size: 0.95rem;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: var(--radius-m);
  cursor: pointer;
  display: none;
  ${PHONE} { display: inline-flex; }
`;

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface SonstigesTitlePickerProps {
  apiClient: BudgetApiClient;
  filters: FilterState;
  parentType: string;
  subType: string | null;
  brokenOutTitleIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  anchorX: number;
  anchorY: number;
  valueFormatter?: (value: number) => string;
}

export function SonstigesTitlePicker({
  apiClient,
  filters,
  parentType,
  subType,
  brokenOutTitleIds,
  onToggle,
  onClose,
  anchorX,
  anchorY,
  valueFormatter = formatTooltipValue,
}: SonstigesTitlePickerProps) {
  const m = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasAutoFocusedRef = useRef(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  const { data, isLoading, error } = useAsync<SonstigesTitle[]>(
    () => apiClient.getSonstigesTitles(filters, parentType, subType),
    [apiClient, parentType, subType, filters],
  );

  const titles = data ?? [];

  // Sort by share of Sonstiges, largest first (the server already returns them
  // this way; re-sorting keeps the order guaranteed regardless of source).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = q
      ? titles.filter(t => t.zweckbestimmung.toLowerCase().includes(q))
      : titles;
    return [...matched].sort((a, b) => b.sharePct - a.sharePct);
  }, [titles, query]);

  const brokenOut = useMemo(() => new Set(brokenOutTitleIds), [brokenOutTitleIds]);

  // Position near the click, flipping sides / clamping to the viewport.
  useEffect(() => {
    const margin = 8;
    let left = anchorX + ANCHOR_OFFSET;
    let top = anchorY - POPOVER_MAX_HEIGHT / 3;

    if (left + POPOVER_WIDTH > window.innerWidth - margin) {
      left = anchorX - POPOVER_WIDTH - ANCHOR_OFFSET;
    }
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    if (top + POPOVER_MAX_HEIGHT > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - POPOVER_MAX_HEIGHT - margin);
    }
    setPosition({ top, left });
    setIsPositioned(true);
  }, [anchorX, anchorY]);

  // Dismiss on outside click (mouseup, so the opening click doesn't close it)
  // and on Escape.
  useEffect(() => {
    const onMouseUp = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  // Move focus into the dialog on open so keyboard / screen-reader users land
  // inside it (Escape still closes from anywhere). Focus the container first;
  // once the list loads, upgrade to the search box when it's shown — but only
  // while focus is still inside the dialog, so we never yank it back from a row
  // the user has already reached.
  useEffect(() => {
    if (isPositioned) containerRef.current?.focus();
  }, [isPositioned]);

  useEffect(() => {
    if (!isPositioned || isLoading || hasAutoFocusedRef.current) return;
    const input = searchInputRef.current;
    if (!input) return;
    const active = document.activeElement;
    if (active === containerRef.current || containerRef.current?.contains(active) || active === document.body) {
      input.focus();
    }
    hasAutoFocusedRef.current = true;
  }, [isPositioned, isLoading]);

  const showSearch = titles.length > 8;
  const selectedCount = titles.filter(t => brokenOut.has(t.id)).length;

  const content = (
    <>
      {/* Scrim: the mouseup listener handles desktop dismiss; on phones this is
          the visible dimming backdrop and its own click closes the sheet. */}
      <Scrim isVisible={isPositioned} onClick={onClose} aria-hidden="true" />
      <Container
        ref={containerRef}
        isVisible={isPositioned}
        role="dialog"
        aria-modal="true"
        aria-label={m.sonstigesPicker.dialogLabel}
        tabIndex={-1}
        style={{
          ['--anchor-top' as string]: `${position.top}px`,
          ['--anchor-left' as string]: `${position.left}px`,
        }}
      >
        <Header>
          <HeaderText>
            <Title>{m.sonstigesPicker.heading}</Title>
            <Subtitle>
              {isLoading
                ? m.sonstigesPicker.loading
                : titles.length > 0
                ? m.sonstigesPicker.count(titles.length)
                : m.sonstigesPicker.noMoreTitles}
            </Subtitle>
          </HeaderText>
          <CloseButton onClick={onClose} aria-label={m.common.close} title={m.common.close}>
            <span className="icon" data-icon="close" />
          </CloseButton>
        </Header>

        {showSearch && (
          <SearchWrap>
            <SearchIcon />
            <SearchInput
              ref={searchInputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={m.sonstigesPicker.searchPlaceholder}
              aria-label={m.sonstigesPicker.searchAria}
            />
          </SearchWrap>
        )}

        {isLoading ? (
          <LoadingWrap><Spinner /></LoadingWrap>
        ) : error ? (
          <StateMsg>{m.sonstigesPicker.loadError}</StateMsg>
        ) : filtered.length === 0 ? (
          <StateMsg>{query ? m.sonstigesPicker.noMatch : m.sonstigesPicker.noMoreInSonstiges}</StateMsg>
        ) : (
          <List>
            <HeaderRow aria-hidden="true">
              <HeaderName>{m.sonstigesPicker.colTitle}</HeaderName>
              <HeaderIst>{m.sonstigesPicker.colIst}</HeaderIst>
              <HeaderShare>{m.sonstigesPicker.colShare}</HeaderShare>
              <HeaderToggleSpacer />
            </HeaderRow>
            {filtered.map(t => {
                const active = brokenOut.has(t.id);
                // € column = the title's highest single-year Ist over the whole
                // period; Anteil column + bar = its share of the Sonstiges bucket.
                return (
                  <Row
                    key={t.id}
                    active={active}
                    aria-pressed={active}
                    onClick={() => onToggle(t.id)}
                    title={active ? m.sonstigesPicker.rowRemove : m.sonstigesPicker.rowAdd}
                  >
                    <RowTop>
                      <RowName>{t.zweckbestimmung}</RowName>
                      <RowIst>{valueFormatter(t.totalValue)}</RowIst>
                      <RowShare>{formatSharePercent(t.sharePct)}</RowShare>
                      <Toggle active={active} aria-hidden="true">
                        {active ? <CheckIcon /> : <PlusIcon />}
                      </Toggle>
                    </RowTop>
                    <ShareTrack>
                      <ShareFill active={active} pct={t.sharePct} />
                    </ShareTrack>
                  </Row>
                );
              })}
          </List>
        )}

        <Footer>
          <FooterCount>
            {selectedCount > 0 ? m.sonstigesPicker.selected(selectedCount) : m.sonstigesPicker.prompt}
          </FooterCount>
          <DoneButton onClick={onClose}>{m.common.done}</DoneButton>
        </Footer>
      </Container>
    </>
  );

  return createPortal(content, document.body);
}
